import { useRef, useState } from "react";
import { ImageUp, Loader2, RefreshCw } from "lucide-react";
import { ACCEPT, SAMPLES } from "@/lib/constants";

export const UploadDropzone = ({ upload, uploading, progress, maxMb, onFile, onSample }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);

  const handle = (files) => files?.[0] && onFile(files[0]);

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={0}
        data-testid="upload-dropzone"
        aria-label="Upload an image: drag and drop or press Enter to browse"
        onClick={() => input.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handle(e.dataTransfer.files);
        }}
        className={`relative overflow-hidden rounded-xl border border-dashed cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a00] ${
          drag ? "border-[#ff5a00] bg-[#ff5a00]/10" : "border-white/15 hover:border-white/30 bg-black/30"
        }`}
      >
        {upload ? (
          <div className="flex gap-3 p-3 items-center">
            <img src={upload.previewUrl} alt="Uploaded original" data-testid="upload-preview-image" className="h-16 w-16 rounded-lg object-contain bg-white/5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-white truncate" data-testid="upload-file-name">{upload.name}</p>
              <p className="font-mono text-[11px] text-zinc-500">{upload.width}×{upload.height}px · {upload.format?.toUpperCase()}</p>
              <p className="text-[11px] text-[#ff7a33] mt-1 flex items-center gap-1"><RefreshCw size={11} /> Click or drop to replace</p>
            </div>
          </div>
        ) : (
          <div className="py-7 px-4 flex flex-col items-center text-center gap-2">
            <ImageUp className="text-zinc-400" size={22} />
            <p className="text-sm text-white">Drop an image or <span className="text-[#ff7a33]">browse</span></p>
            <p className="text-[11px] text-zinc-500">PNG, JPG, JPEG, WEBP · max {maxMb} MB</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center gap-2 text-sm" data-testid="upload-progress">
            <Loader2 className="animate-spin text-[#ff5a00]" size={16} /> Uploading {progress}%
          </div>
        )}
        <input
          ref={input}
          type="file"
          accept={ACCEPT.join(",")}
          className="hidden"
          data-testid="upload-file-input"
          onChange={(e) => {
            handle(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      <div>
        <p className="eyebrow mb-2">Or try a sample</p>
        <div className="grid grid-cols-4 gap-2">
          {SAMPLES.map((s) => (
            <button
              key={s.key}
              type="button"
              data-testid={`sample-${s.key}-btn`}
              onClick={() => onSample(s)}
              title={s.label}
              className="group rounded-lg overflow-hidden border border-white/10 hover:border-[#ff5a00] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a00]"
            >
              <img src={s.file} alt={s.label} className="aspect-square w-full object-cover bg-white transition-transform duration-300 group-hover:scale-105" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
